import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Upload } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import { adoptionsApi, formatApiError } from "@/lib/api";

const CAT_LABEL = {
    handicap: "Handicap-Tauben",
    flugfaehig: "Flugfähige Tauben",
    partner_gesucht: "Partner gesucht",
};

export default function AdoptionEdit() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [doc, setDoc] = useState(null);
    const [err, setErr] = useState(null);
    const [newImages, setNewImages] = useState([]);
    const [replace, setReplace] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [messages, setMessages] = useState([]);

    useEffect(() => {
        adoptionsApi
            .getByToken(token)
            .then(setDoc)
            .catch((e) => setErr(formatApiError(e?.response?.data?.detail)))
            .finally(() => setLoading(false));
        adoptionsApi.messagesByToken(token).then((d) => setMessages(d.messages || [])).catch(() => {});
    }, [token]);

    function set(k, v) {
        setDoc((d) => ({ ...d, [k]: v }));
    }

    async function save(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { compressImage } = await import("@/lib/imageCompress");
            const compressed = await Promise.all(
                newImages.map((f) => (f.type?.startsWith("image/") ? compressImage(f) : Promise.resolve(f)))
            );
            const fd = new FormData();
            ["title", "category", "description", "plz", "location", "health_status", "sex", "status"].forEach((k) => {
                if (doc[k] !== undefined && doc[k] !== null) fd.append(k, String(doc[k]));
            });
            fd.append("flight_capable", String(!!doc.flight_capable));
            fd.append("replace_images", String(replace));
            compressed.forEach((f) => fd.append("images", f));
            await adoptionsApi.updateByToken(token, fd);
            toast.success("Änderungen gespeichert");
            setNewImages([]);
            const fresh = await adoptionsApi.getByToken(token);
            setDoc(fresh);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    }

    async function remove() {
        if (!window.confirm("Inserat wirklich endgültig löschen?")) return;
        try {
            await adoptionsApi.deleteByToken(token);
            toast.success("Inserat gelöscht");
            navigate("/vermittlung");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    }

    if (loading) {
        return (
            <SiteLayout>
                <div className="max-w-3xl mx-auto px-4 py-24 text-[#94a3b8] label gentle-pulse">Wird geladen…</div>
            </SiteLayout>
        );
    }
    if (err || !doc) {
        return (
            <SiteLayout>
                <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="edit-token-error">
                    <h1 className="font-display text-3xl mb-4">Link ungültig oder abgelaufen</h1>
                    <p className="text-[#94a3b8] mb-8">{err || "Bitte fordere einen neuen Bearbeitungs-Link an."}</p>
                    <Link to="/vermittlung" className="btn-secondary inline-flex">
                        <ArrowLeft size={14} /> Zurück
                    </Link>
                </div>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <Link to={`/vermittlung/${doc.id}`} className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-[#f0c674] text-sm mb-10">
                    <ArrowLeft size={14} /> Öffentliches Profil ansehen
                </Link>
                <div className="label mb-3">Mein Inserat</div>
                <h1 className="font-display text-4xl sm:text-5xl mb-2">{doc.title}</h1>
                <p className="text-[#94a3b8] mb-10">
                    {CAT_LABEL[doc.category] || doc.category}
                    {doc.status === "archived" && (
                        <span className="ml-3 text-xs font-mono uppercase tracking-widest text-[#94a3b8]">· archiviert</span>
                    )}
                </p>

                <form onSubmit={save} className="space-y-5" data-testid="edit-form">
                    <Field label="Titel">
                        <input value={doc.title || ""} onChange={(e) => set("title", e.target.value)} className="input-field" data-testid="edit-title" />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                        <Field label="Kategorie">
                            <select value={doc.category} onChange={(e) => set("category", e.target.value)} className="input-field" data-testid="edit-category">
                                {Object.entries(CAT_LABEL).map(([k, v]) => (
                                    <option key={k} value={k} className="bg-[#0c1222]">{v}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Geschlecht">
                            <select value={doc.sex || "unbekannt"} onChange={(e) => set("sex", e.target.value)} className="input-field">
                                <option value="unbekannt" className="bg-[#0c1222]">Unbekannt</option>
                                <option value="m" className="bg-[#0c1222]">Männlich</option>
                                <option value="w" className="bg-[#0c1222]">Weiblich</option>
                            </select>
                        </Field>
                        <Field label="Flugfähig?">
                            <label className="flex items-center gap-2 input-field cursor-pointer">
                                <input type="checkbox" checked={!!doc.flight_capable} onChange={(e) => set("flight_capable", e.target.checked)} className="accent-[#f0c674]" />
                                Flugfähig
                            </label>
                        </Field>
                    </div>
                    <Field label="Beschreibung">
                        <textarea value={doc.description || ""} onChange={(e) => set("description", e.target.value)} className="input-field" rows={5} data-testid="edit-description" />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="PLZ">
                            <input value={doc.plz || ""} onChange={(e) => set("plz", e.target.value)} className="input-field" />
                        </Field>
                        <Field label="Ort">
                            <input value={doc.location || ""} onChange={(e) => set("location", e.target.value)} className="input-field" />
                        </Field>
                    </div>
                    <Field label="Gesundheitszustand">
                        <input value={doc.health_status || ""} onChange={(e) => set("health_status", e.target.value)} className="input-field" />
                    </Field>
                    <Field label="Status">
                        <select value={doc.status || "active"} onChange={(e) => set("status", e.target.value)} className="input-field">
                            <option value="active" className="bg-[#0c1222]">Aktiv — öffentlich sichtbar</option>
                            <option value="archived" className="bg-[#0c1222]">Archiviert — nicht sichtbar</option>
                        </select>
                    </Field>

                    <div className="glass p-5">
                        <div className="label mb-3">Bilder ({doc.image_count} aktuell)</div>
                        <label htmlFor="new-images" className="cursor-pointer flex items-center gap-3 input-field hover:border-[#f0c674]/40">
                            <Upload size={16} className="text-[#f0c674]" />
                            <span className="text-sm text-[#94a3b8]">
                                {newImages.length > 0 ? `${newImages.length} neue Datei(en) ausgewählt` : "Weitere Bilder hochladen"}
                            </span>
                        </label>
                        <input id="new-images" type="file" multiple accept="image/*" onChange={(e) => setNewImages(Array.from(e.target.files || []).slice(0, 8))} className="hidden" />
                        <label className="flex items-center gap-2 text-xs text-[#94a3b8] mt-3 cursor-pointer">
                            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="accent-[#f0c674]" />
                            Vorhandene Bilder durch die neuen ersetzen (statt anhängen)
                        </label>
                    </div>

                    <div className="flex flex-wrap gap-3 pt-4">
                        <button type="submit" disabled={submitting} className="btn-primary" data-testid="edit-save">
                            <Save size={14} /> {submitting ? "Speichert…" : "Speichern"}
                        </button>
                        <button type="button" onClick={remove} className="btn-ghost text-rose-300" data-testid="edit-delete">
                            <Trash2 size={14} /> Inserat löschen
                        </button>
                    </div>
                </form>

                {/* Inbox */}
                <div className="mt-16">
                    <div className="label mb-3">Anfragen zu deinem Inserat</div>
                    <h2 className="font-display text-2xl mb-6">
                        {messages.length} {messages.length === 1 ? "Nachricht" : "Nachrichten"}
                    </h2>
                    {messages.length === 0 ? (
                        <div className="glass p-8 text-center text-[#94a3b8]">
                            Noch keine Anfragen. Sobald jemand Interesse zeigt, erscheinen die Nachrichten hier.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {messages.map((m) => (
                                <div key={m.id} className="glass p-5" data-testid={`msg-${m.id}`}>
                                    <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                                        <div>
                                            <div className="font-display text-lg">{m.sender_name}</div>
                                            <div className="text-xs text-[#64748b]">
                                                {new Date(m.created_at).toLocaleString("de-DE")} ·{" "}
                                                <a href={`mailto:${m.sender_email}`} className="text-[#f0c674]">{m.sender_email}</a>
                                                {m.sender_phone ? ` · ${m.sender_phone}` : ""}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-sm text-[#94a3b8] mt-2 whitespace-pre-wrap">{m.message}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </SiteLayout>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-sm text-[#94a3b8] mb-2">{label}</span>
            {children}
        </label>
    );
}
